import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = ["onClick", "aria-label", "data-test", "data-testid"]
const _hoisted_2 = ["data-title"]
const _hoisted_3 = {
  class: "ds-segmented-picker__text-inner",
  "aria-hidden": "true",
  "data-test": "segmented-picker-text"
}

import { useSlots, ref, provide, watch, useTemplateRef, onMounted, onBeforeUnmount, computed } from "vue";
import type { Theme } from "../types/theme";
import { dsSegmentedInjectedKey } from "./ds-segmented-injected-key";
import { CustomEaseDurations, CustomEaseNames, useGsap } from "@sazka/utils/gsap-register";


export default /*@__PURE__*/_defineComponent({
  __name: 'ds-segmented-picker',
  props: /*@__PURE__*/_mergeModels({
    theme: {},
    isFullWidth: { type: Boolean, default: false },
    align: { default: "left" },
    useFor: { type: Boolean, default: false },
    size: { default: "sm" },
    dataTestValue: {}
  }, {
    "modelValue": { required: false },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["toggleClick"], ["update:modelValue"]),
  setup(__props: any, { expose: __expose, emit: __emit }) {

const navEl = useTemplateRef("navEl");
const pickerSlider = useTemplateRef("pickerSlider");
const segmentButtons = ref<HTMLElement[]>([]);

__expose({
    navEl,
});

const props = __props;

const modelValue = _useModel<string>(__props, "modelValue");

const emit = __emit;

const slots = useSlots();

const tabTitles = computed<string[]>(() => {
    // https://stackoverflow.com/questions/70727383/using-v-for-inside-component-that-uses-slot
    // We need to override internal types from vue to make it work here
    return (
        props.useFor
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              (slots.default()[0].children as any).map((tab) => tab.props?.title)
            : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              slots.default().map((tab) => tab.props?.title)
    ).filter(Boolean); // filter out empty titles (e.g. v-if on PickerItem)
});

const tabNames = computed<string[]>(() => {
    // https://stackoverflow.com/questions/70727383/using-v-for-inside-component-that-uses-slot
    return props.useFor
        ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          (slots.default()[0].children as any).map((tab) => tab.props?.name)
        : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          slots.default().map((tab) => tab.props?.name);
});

const tabDataTestids = computed<string[]>(() => {
    // https://stackoverflow.com/questions/70727383/using-v-for-inside-component-that-uses-slot
    // We need to override internal types from vue to make it work here
    return (
        props.useFor
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              (slots.default()[0].children as any).map((tab) => tab.props?.dataTestid)
            : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              slots.default().map((tab) => tab.props?.dataTestid)
    ).filter(Boolean); // filter out empty data-testids (e.g. v-if on PickerItem)
});

const activeIndex = ref(0);
const selectedTabKey = computed(() => tabNames.value[activeIndex.value]);

provide(dsSegmentedInjectedKey, selectedTabKey);

let buttonWidthsCache: number[] = [];
const calculateWidths = (entries: ResizeObserverEntry[]) => {
    buttonWidthsCache = entries.map((entry) => entry.borderBoxSize[0].inlineSize);
};

const toggleClick = (index: number) => {
    activeIndex.value = index;
    emit("toggleClick");
};

const { gsap } = useGsap();
let sliderTweenAnimation: gsap.core.Tween = null;
const moveSegment = async (index: number, withoutAnimation = false) => {
    const currentButton = segmentButtons.value[index];
    if (!currentButton) {
        return;
    }

    const currentWidth = buttonWidthsCache[index];
    pickerSlider.value.style.width = `${currentWidth}px`;

    // Calculate total width using pre-calculated widths
    const totalWidth = buttonWidthsCache.slice(0, index).reduce((acc, width) => acc + width, 0);

    let duration = CustomEaseDurations.MOTION_DURATION_250;
    if (withoutAnimation) {
        duration = 0;
    }
    if (sliderTweenAnimation) {
        sliderTweenAnimation.kill();
    }

    sliderTweenAnimation = gsap.to(pickerSlider.value, {
        translateX: totalWidth,
        duration: duration,
        ease: CustomEaseNames.MOTION_EASE_OUT,
        onComplete: () => {
            sliderTweenAnimation = null;
        },
    });
};

if (modelValue?.value) {
    watch(
        modelValue,
        (newModelValue) => {
            if (newModelValue) {
                const currentIndex = tabNames.value.indexOf(newModelValue);
                if (currentIndex !== -1) {
                    activeIndex.value = currentIndex;
                    return;
                }
            } else {
                // Reset to the first available tab if modelValue is invalid
                activeIndex.value = 0;
            }
        },
        { immediate: true }
    );
}

const normalizeText = (text: string): string => {
    return text
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
};

let resizeObserver: ResizeObserver = null;
onMounted(async () => {
    // Resize observer will setup initial values
    resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        calculateWidths(entries);
        moveSegment(activeIndex.value, true);
    });

    segmentButtons.value.forEach((segmentButton) => resizeObserver.observe(segmentButton));

    watch(activeIndex, (newActiveIndex) => {
        moveSegment(newActiveIndex);
        modelValue.value = tabNames.value[newActiveIndex];
    });
});

onBeforeUnmount(() => {
    if (resizeObserver) resizeObserver.disconnect();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["ds-segmented-picker", [`ds-segmented-picker--align-${_ctx.align}`, `ds-segmented-picker--${_ctx.size}`]])
  }, [
    _createElementVNode("nav", {
      class: _normalizeClass(["ds-segmented-picker__nav", { 'is-dark': _ctx.theme === 'dark', 'is-full': _ctx.isFullWidth }]),
      ref_key: "navEl",
      ref: navEl
    }, [
      _createElementVNode("div", {
        class: "ds-segmented-picker__slider",
        ref_key: "pickerSlider",
        ref: pickerSlider
      }, null, 512),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabTitles.value, (title, index) => {
        return (_openBlock(), _createElementBlock("button", {
          class: _normalizeClass(["ds-segmented-picker__toggle", {
                    'is-active': index === activeIndex.value,
                    'is-dark': _ctx.theme === 'dark',
                }]),
          key: title,
          onClick: ($event: any) => (toggleClick(index)),
          ref_for: true,
          ref_key: "segmentButtons",
          ref: segmentButtons,
          "aria-label": title,
          "data-test": _ctx.dataTestValue ? `${_ctx.dataTestValue}-${normalizeText(title)}` : null,
          "data-testid": tabDataTestids.value ? tabDataTestids.value[index] : null
        }, [
          _createElementVNode("span", {
            class: "ds-segmented-picker__text",
            "data-title": title
          }, [
            _createElementVNode("span", _hoisted_3, _toDisplayString(title), 1)
          ], 8, _hoisted_2)
        ], 10, _hoisted_1))
      }), 128))
    ], 2),
    _renderSlot(_ctx.$slots, "default")
  ], 2))
}
}

})